import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { routes } from 'flow/types/routes';
import { Contact } from 'flow/types/Contact';
import { TypedMap } from 'flow/types/TypedMap';
import { useHistory } from 'react-router-dom';
import { extractName } from 'flow/utils';
import { State } from 'flow/types/State';
import { LocationType } from 'flow/types/Location';
import {
    fetchContact,
    fetchEditableContact,
} from 'flow/actions/contact-actions';
import { getIsSinglePaneMode } from 'flow/selectors/selectors';
import { selectExtensions } from 'flow/selectors/ExtensionSelectors';
import Page from 'flow/components/Setting/Page';
import ContactHeader from 'flow/components/contact-card/ContactHeader';
import ContactInfoCard from 'flow/components/contact-card/ContactInfoCard';
import ContactSettingsCard from 'flow/components/contact-card/ContactSettingsCard';
import ContactScheduleCard from 'flow/components/contact-card/ContactScheduleCard';
import { ContactKey } from 'flow/types/EntityKeys';
import { createSelector } from 'reselect';
import { Extension } from 'flow/types/Extension';
import ContactCallHistoryWidgetTW from 'flow/components/contact-card/ContactCallHistoryWidgetTW';

interface Props {
    onNavigation?: () => void;
    contact: TypedMap<Contact>;
    calendarPath?: string;
}

const ContactView = ({ onNavigation, contact, calendarPath }: Props) => {
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();

    const contactKey = contact?.get('key');

    const isSinglePaneMode = useAppSelector(getIsSinglePaneMode);
    const extensionKey = useAppSelector((state) =>
        selectExtensionKeyFromContactKey(state, contactKey),
    );

    useEffect(() => {
        if (contactKey) {
            dispatch(fetchContact(contactKey));
            dispatch(fetchEditableContact(contactKey));
        }
    }, [dispatch, contactKey]);

    const propsWithKeys = {
        contactKey,
        extensionKey,
        contact,
        onNavigation,
    };

    return (
        <Page
            childrenClassName="w-full"
            header={extractName(contact)}
            onClickBack={
                history.location.state?.from
                    ? () => history.push(history.location.state?.from)
                    : isSinglePaneMode
                      ? () => history.push(routes['tickets'])
                      : undefined
            }
            discardHeader={true}
            useContactCardStyles={true}
        >
            <div className="flex flex-col items-center">
                <div className="@container relative flex w-full max-w-screen-md flex-col gap-y-6">
                    <ContactHeader {...propsWithKeys} />
                    <div className="flex flex-col items-start gap-6 @2xl:flex-row">
                        <ContactInfoCard {...propsWithKeys} />
                        <div className="flex w-full min-w-1/2 flex-col gap-6 @2xl:w-auto">
                            <ContactSettingsCard {...propsWithKeys} />
                            <ContactScheduleCard
                                {...propsWithKeys}
                                calendarPath={calendarPath}
                            />
                            <ContactCallHistoryWidgetTW {...propsWithKeys} />
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default React.memo(ContactView);

const selectExtensionKeyFromContactKey = createSelector(
    [selectExtensions, (_state: State, contactKey: ContactKey) => contactKey],
    (extensions, contactKey) => {
        if (!contactKey) return null;
        const extension: TypedMap<Extension> = extensions.find(
            (extension) => extension.getIn(['contact', 'key']) === contactKey,
        );
        return extension?.get('key');
    },
);
